import React, { useState, useCallback, useEffect } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { isArray } from "../../helpers/methods";
import NewNumberInput from "../Common/NewNumberInput";
import {
  useIsBusinessPlan,
  useIsEnterprisePlan,
} from "../../helpers/subscriptionPlan";

const HOBBYIST_MAX_ROWS = 2;
const BUSINESS_MAX_ROWS = 6;

export const validateTierPrice = (tpStr = "") => {
  const prices = tpStr.split(",");
  const invalidPrices = prices.filter((p) => {
    const [minQuantity, maxQuantity, price] = p.split("|");
    return (
      isNaN(minQuantity) ||
      isNaN(maxQuantity) ||
      isNaN(price) ||
      Number(minQuantity) <= 0 || //quantity must be larger than 0
      Number(maxQuantity) <= 0 ||
      minQuantity > maxQuantity
    );
  });
  return invalidPrices.length === 0;
};

export const parseTierPriceStringToJSON = (inputString) => {
  const items = inputString.split(",");

  const result = items
    .map((item) => {
      const [minQuantity, maxQuantity, price] = item.split("|").map(Number);
      return {
        minQuantity: isNaN(minQuantity) ? undefined : minQuantity,
        maxQuantity: isNaN(maxQuantity) ? undefined : maxQuantity,
        price: isNaN(price) ? undefined : price,
      };
    })
    .filter((tp) => {
      //conversion only take valid tier prices
      const { minQuantity, maxQuantity, price } = tp;
      return !(
        minQuantity === undefined ||
        maxQuantity === undefined ||
        price === undefined
      );
    });

  return result;
};

export const convertTierPriceToString = (rows) => {
  return rows
    .map((item) => `${item.minQuantity}|${item.maxQuantity}|${item.price}`)
    .join(",");
};

const Row = ({ index, data, onChange, onRemove }) => {
  const isBusinessPlan = useIsBusinessPlan();
  const isEnterprisePlan = useIsEnterprisePlan();

  const disableByPlan =
    (!isBusinessPlan && index >= HOBBYIST_MAX_ROWS) ||
    (isBusinessPlan && !isEnterprisePlan && index >= BUSINESS_MAX_ROWS);

  const handleChange = useCallback(
    (field, value) => {
      onChange(index, { ...data, [field]: value });
    },
    [data, index, onChange]
  );

  const handleChangeQuantity = useCallback(
    (field, value) => {
      onChange(index, { ...data, minQuantity: value, maxQuantity: value });
    },
    [data, index, onChange]
  );

  return (
    <div
      style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
    >
      <NewNumberInput
        label="Quantity"
        variant="outlined"
        value={data.minQuantity}
        onChangeValue={(v) => {
          handleChangeQuantity("minQuantity", v);
        }}
        style={{ marginRight: "10px" }}
        required
        disabled={disableByPlan}
      />
      <NewNumberInput
        label="Total Price"
        variant="outlined"
        value={data.price}
        decimals={2}
        onChangeValue={(v) => handleChange("price", v)}
        style={{ marginRight: "10px" }}
        required
        disabled={disableByPlan}
      />
      <IconButton onClick={() => onRemove(index)} aria-label="delete">
        <DeleteIcon />
      </IconButton>
    </div>
  );
};

const TierPrice = (props) => {
  const { value = "", onChange = () => {} } = props;
  const isBusinessPlan = useIsBusinessPlan();
  const isEnterprisePlan = useIsEnterprisePlan();
  const tierPrices = parseTierPriceStringToJSON(value ? value : "");
  const initialData =
    isArray(tierPrices) && tierPrices.length > 0 ? tierPrices : [];
  const [rows, setRows] = useState(initialData);

  const handleChange = useCallback(
    (index, rowData) => {
      const newRows = [...rows];
      newRows[index] = rowData;
      setRows(newRows);
    },
    [rows]
  );

  const handleAddRow = useCallback(() => {
    setRows([...rows, { minQuantity: "", maxQuantity: "", price: "" }]);
  }, [rows]);

  const handleRemoveRow = useCallback(
    (index) => {
      const newRows = [...rows];
      newRows.splice(index, 1);
      setRows(newRows);
    },
    [rows]
  );

  useEffect(() => {
    if (rows) {
      onChange(rows);
    }
  }, [onChange, rows]);

  const disableByPlan =
    (!isBusinessPlan && rows.length >= HOBBYIST_MAX_ROWS) ||
    (isBusinessPlan && !isEnterprisePlan && rows.length >= BUSINESS_MAX_ROWS);

  return (
    <div>
      <Button
        variant="contained"
        disabled={disableByPlan}
        onClick={handleAddRow}
        style={{ marginTop: "10px" }}
      >
        Add Row
      </Button>
      {disableByPlan && <span>You've reached your plan maximum.</span>}
      {rows.map((data, index) => (
        <Row
          key={index}
          index={index}
          data={data}
          onChange={handleChange}
          onRemove={handleRemoveRow}
        />
      ))}
    </div>
  );
};

export default TierPrice;
